import React from 'react';

function valueOf(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

function RegionExitEditor({
  currentRegionId,
  exits,
  onAdd,
  onChange,
  onPlace,
  onRemove,
  placingExitKey,
  regions,
  spawnableItems = [],
}) {
  return (
    <section className="world-editor-section" aria-labelledby="region-exits-title">
      <div className="world-editor-section__heading">
        <div>
          <h3 id="region-exits-title">Region Exits</h3>
          <p>
            Create and save both reciprocal exits while disabled, then enable
            the first side and finally its return exit.
          </p>
        </div>
        <button className="button-secondary" type="button" onClick={onAdd}>
          Add Exit
        </button>
      </div>

      {exits.length === 0 && (
        <p className="world-editor-empty">No adjacent region exits configured.</p>
      )}

      <div className="world-editor-card-list">
        {exits.map((exit, index) => {
          const prefix = `region-exit-${index}`;
          const isPlacing = placingExitKey === exit.key;
          return (
            <details className="world-editor-card" key={`region-exit-${index}`}>
              <summary>
                <span>{exit.label || exit.key || `Exit ${index + 1}`}</span>
                <span className={exit.enabled ? 'status-enabled' : 'status-disabled'}>
                  {exit.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </summary>
              <div className="world-editor-card__body">
                <label className="form-control" htmlFor={`${prefix}-key`}>
                  <span>Key</span>
                  <input
                    id={`${prefix}-key`}
                    value={exit.key}
                    onChange={(event) => onChange(index, { key: event.target.value })}
                  />
                </label>
                <label className="form-control" htmlFor={`${prefix}-label`}>
                  <span>Label</span>
                  <input
                    id={`${prefix}-label`}
                    value={exit.label}
                    onChange={(event) => onChange(index, { label: event.target.value })}
                    placeholder="Ravine gate"
                  />
                </label>
                <label className="form-control" htmlFor={`${prefix}-target-region`}>
                  <span>Target region</span>
                  <select
                    id={`${prefix}-target-region`}
                    value={exit.targetRegionId}
                    onChange={(event) => onChange(index, {
                      targetRegionId: event.target.value,
                    })}
                  >
                    <option value="">Choose a region…</option>
                    {regions
                      .filter(region => (
                        valueOf(region, 'id', 'Id') !== currentRegionId
                      ))
                      .map(region => (
                        <option
                          key={valueOf(region, 'id', 'Id')}
                          value={valueOf(region, 'id', 'Id')}
                        >
                          {valueOf(region, 'name', 'Name')
                            || valueOf(region, 'id', 'Id')}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="form-control" htmlFor={`${prefix}-target-key`}>
                  <span>Target exit key</span>
                  <input
                    id={`${prefix}-target-key`}
                    value={exit.targetExitKey}
                    onChange={(event) => onChange(index, {
                      targetExitKey: event.target.value,
                    })}
                    placeholder="return_gate"
                  />
                </label>
                <label className="form-control" htmlFor={`${prefix}-required-unlock`}>
                  <span>Required run unlock</span>
                  <input
                    id={`${prefix}-required-unlock`}
                    value={exit.requiredRunUnlockKey}
                    onChange={(event) => onChange(index, {
                      requiredRunUnlockKey: event.target.value,
                    })}
                    placeholder="dizzy_dunes_water_authority"
                  />
                </label>
                <div className="world-editor-coordinate-grid">
                  <label className="form-control" htmlFor={`${prefix}-toll-item`}>
                    <span>First-crossing toll</span>
                    <select
                      id={`${prefix}-toll-item`}
                      value={exit.tollItemId}
                      onChange={(event) => onChange(index, {
                        tollItemId: event.target.value,
                      })}
                    >
                      <option value="">No toll</option>
                      {spawnableItems.map(item => {
                        const itemId = valueOf(item, 'itemId', 'ItemId');
                        return (
                          <option key={itemId} value={itemId}>
                            {valueOf(item, 'name', 'Name') || itemId}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <label className="form-control" htmlFor={`${prefix}-toll-quantity`}>
                    <span>Quantity</span>
                    <input
                      id={`${prefix}-toll-quantity`}
                      min="0"
                      type="number"
                      value={exit.tollQuantity}
                      onChange={(event) => onChange(index, {
                        tollQuantity: Math.max(
                          0,
                          Number(event.target.value) || 0,
                        ),
                      })}
                    />
                  </label>
                </div>
                <label className="form-control" htmlFor={`${prefix}-toll-unlock`}>
                  <span>Toll-paid run unlock</span>
                  <input
                    id={`${prefix}-toll-unlock`}
                    value={exit.tollPaidRunUnlockKey}
                    onChange={(event) => onChange(index, {
                      tollPaidRunUnlockKey: event.target.value,
                    })}
                    placeholder="rotgut_water_toll_paid"
                  />
                </label>
                <div className="world-editor-coordinate-grid">
                  <label className="form-control" htmlFor={`${prefix}-x`}>
                    <span>X</span>
                    <input
                      id={`${prefix}-x`}
                      min="0"
                      type="number"
                      value={exit.x}
                      onChange={(event) => onChange(index, {
                        x: Number(event.target.value),
                      })}
                    />
                  </label>
                  <label className="form-control" htmlFor={`${prefix}-y`}>
                    <span>Y</span>
                    <input
                      id={`${prefix}-y`}
                      min="0"
                      type="number"
                      value={exit.y}
                      onChange={(event) => onChange(index, {
                        y: Number(event.target.value),
                      })}
                    />
                  </label>
                </div>
                <label className="world-editor-checkbox" htmlFor={`${prefix}-enabled`}>
                  <input
                    checked={exit.enabled}
                    id={`${prefix}-enabled`}
                    type="checkbox"
                    onChange={(event) => onChange(index, {
                      enabled: event.target.checked,
                    })}
                  />
                  <span>Players may use this exit</span>
                </label>
                <div className="world-editor-card__actions">
                  <button
                    className={`button-secondary ${isPlacing ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => onPlace(exit.key)}
                  >
                    {isPlacing ? 'Click a map tile…' : 'Place on Map'}
                  </button>
                  <button
                    className="button-critical"
                    type="button"
                    onClick={() => onRemove(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}

export default RegionExitEditor;
